import type { CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';
import { Button, Dialog, Empty, Icon, Input, Menu, Panes, Select } from '@wtfalch/design';
import type {
  Attachment,
  DraftAttachment,
  MailboxNode,
  MailClient,
  MailError,
  MailIdentity,
  Message,
  ThreadDetail,
} from '@wtfalch/email/mailbox';
import { downloadAttachment, forwardDraft, replyDraft, walk } from '@wtfalch/email/mailbox';
import { Fragment, useEffect, useState } from 'react';

import {
  isPlainClick,
  navigate,
  parse,
  parseRoomUrl,
  pathForCalendar,
  pathForRoute,
} from '../routes.ts';
import { Avatar } from '../ui/Avatar.tsx';
import { formatMessageWhen } from '../ui/format.ts';
import { PersonIdentity } from '../ui/people.tsx';
import { AttachButton, AttachmentChips, useAttachments } from './Attachments.tsx';
import type { ComposeInitial } from './Compose.tsx';
import { CounterAttachment } from './CounterCard.tsx';
import { eventDateParam, findEventByUid, icsUid } from './counter.ts';
import {
  bodyParagraphs,
  collapsedIndices,
  displayName,
  formatStarted,
  hiddenMessageCount,
  joinParticipants,
  labelProblem,
  linkify,
  participantsOf,
  snoozeOptions,
} from './format.ts';
import { InvitationCard } from './InvitationCard.tsx';
import { ArchiveIcon } from './icons.tsx';

/** The open agora, read off the address bar rather than threaded down as a
 *  prop: `ThreadPane` renders only under `/h/<agoraId>/mail/...`, so the
 *  current path already names it, the same fact `App.tsx`'s own routing
 *  already rests on. Keeps "Open in Calendar" (agora#222) from adding an
 *  `agoraId` prop through `Mail.tsx` for a value the address already carries. */
function currentAgoraId(): string | null {
  const route = parse(window.location.pathname);
  return route.kind === 'agora' ? route.agoraId : null;
}

/** An `.ics` (or a message declaring `text/calendar`) is a calendar
 *  invitation: its chip gets a calendar glyph, and `InviteExtras` adds the
 *  "Going?" card and "Open in Calendar" beside it. */
function isInvite(attachment: Attachment): boolean {
  return attachment.type === 'text/calendar' || attachment.name.toLowerCase().endsWith('.ics');
}

/** `mail.css`'s fold to one column. */
const PHONE_QUERY = 'not all and (min-width: 860px)';

/** The hold window `Mail.tsx` sends a reply with (its `REPLY_HOLD_SECONDS`). */
const REPLY_HOLD_MS = 10_000;

export function ThreadPane({
  detail,
  error,
  me,
  meName,
  identity,
  tree,
  client,
  ownClient,
  calendarClient,
  onBack,
  onArchive,
  onTrash,
  onStar,
  onMarkUnread,
  onMoveTo,
  onSnooze,
  labels = [],
  allLabels = [],
  onLabel,
  onReply,
  onExpand,
  onDownload,
  onRetry,
  busy,
}: {
  detail: ThreadDetail | null;
  error: MailError | null;
  me: string;
  /** The signed-in person's own name, from the mail SDK's identities --
   *  for the reply box's own avatar, which used to draw seeded on the bare
   *  address alone and read as a single "M". */
  meName: string | null;
  /** The signed-in identity, for sending a `DECLINECOUNTER` as the reply
   *  it is (#284's `CounterCard`) -- `null` before the mail SDK's own
   *  identities have loaded, same as `meName`. */
  identity: MailIdentity | null;
  tree: readonly MailboxNode[] | null;
  /** This thread's own account -- a shared account's client while viewing
   *  one of its folders. Reads and downloads go through this. */
  client: MailClient;
  /** The signed-in person's own account, whatever `client` is (agora#485):
   *  a reply always goes out from this person (`Mail.tsx`'s `handleReply`
   *  sends through this same client), so its attachment must upload here
   *  too -- uploading through a shared `client` instead left the reply's
   *  send referencing a blob id the sending account could not read. */
  ownClient: MailClient;
  /** The calendar client already in the app (`App.tsx`'s own
   *  `calendarClient`) -- #284's `CounterCard` reads the event a `COUNTER`
   *  proposes a new time for through it, and writes an Accept back the
   *  same way `calendar/Event.tsx`'s RSVP does. */
  calendarClient: CalendarClient;
  /** A phone's back control, out of the thread and onto the list -- see
   *  `mail.css`'s phone media query, which is what actually decides
   *  whether the list or the pane is on screen. */
  onBack: () => void;
  onArchive: (threadId: string) => void;
  onTrash: (threadId: string) => void;
  onStar: (threadId: string, flagged: boolean) => void;
  onMarkUnread: (threadId: string) => void;
  onMoveTo: (threadId: string, mailboxId: string) => void;
  /** Takes the thread out of the Inbox until `until`. */
  onSnooze: (threadId: string, until: Date) => void;
  /** The labels on this thread, and every label in use on the account. */
  labels: readonly string[];
  allLabels: readonly string[];
  /** Resolves with why the label was refused, or `null` once it is set. The
   *  error has already been reported; the answer is for the dialog. */
  onLabel: (threadId: string, label: string, on: boolean) => Promise<string | null>;
  /** Sends the reply, held for `Mail.tsx`'s own delay -- the returned
   *  `cancel` stays good for that whole window. Rejects when the reply
   *  cannot be sent; the reply box shows why. */
  onReply: (
    message: Message,
    text: string,
    all: boolean,
    attachments: readonly DraftAttachment[],
  ) => Promise<{ cancel: () => Promise<void> }>;
  /** The reply box's own expand button: opens the full composer, seeded
   *  with this reply's recipients, subject and text-so-far. */
  onExpand: (initial: ComposeInitial) => void;
  onDownload: (attachment: Attachment) => void;
  /** Fetches the open thread again, for the reader's "Try again". */
  onRetry: () => void;
  busy: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const [replyError, setReplyError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyAll, setReplyAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<{ cancel: () => Promise<void> } | null>(null);
  // `ownClient`, not `client`: the reply goes out from this person even
  // inside a shared folder, so its attachment has to upload to the same
  // account the send itself uses (agora#485).
  const reply = useAttachments(ownClient);
  const clearReplyAttachments = reply.clear;
  const [narrow, setNarrow] = useState(false);
  const [labelling, setLabelling] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [labelError, setLabelError] = useState('');
  useEffect(() => {
    const query = window.matchMedia?.(PHONE_QUERY);
    if (!query) return;
    const sync = () => setNarrow(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  // The reply stays in the box for the whole hold window, so cancelling the
  // send leaves it to edit; only the window closing, with the mail gone,
  // empties the box.
  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => {
      setPending(null);
      setReplyText('');
      clearReplyAttachments();
    }, REPLY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [pending, clearReplyAttachments]);

  if (error) {
    return (
      <Panes.Column flex className="mail-reader">
        <div className="grid flex-1 place-items-center p-8 mail-pane-status">
          <Empty icon="mail">
            <p className="text-sm text-muted m-0">{error.message}</p>
            <Button size="sm" onPress={onRetry}>
              Try again
            </Button>
          </Empty>
        </div>
      </Panes.Column>
    );
  }
  if (!detail) {
    return (
      <Panes.Column flex className="mail-reader">
        <div className="grid flex-1 place-items-center p-8 mail-pane-status">
          <p className="text-sm text-muted">Loading…</p>
        </div>
      </Panes.Column>
    );
  }

  const messages = detail.messages;
  const last = messages[messages.length - 1];
  if (!last) return null;
  const participants = participantsOf(messages);
  const isFlagged = messages.some((m) => m.isFlagged);
  const hidden = hiddenMessageCount(messages.length, showAll);
  const collapsed = new Set(collapsedIndices(messages.length, showAll));
  const isOpened = (id: string) => openIds.has(id);

  const moveItems = tree
    ? [...walk(tree)].map((m) => ({
        id: m.id,
        label: m.name,
        onAction: () => onMoveTo(detail.id, m.id),
      }))
    : [];

  const snoozeItems = snoozeOptions().map((o) => ({
    id: `snooze-${o.id}`,
    label: o.label,
    onAction: () => onSnooze(detail.id, o.at),
  }));

  const labelItems = [
    ...[...new Set([...allLabels, ...labels])].sort().map((label) => {
      const on = labels.includes(label);
      return {
        id: `label-${label}`,
        label,
        ...(on ? { icon: 'check' as const } : {}),
        onAction: () => void onLabel(detail.id, label, !on),
      };
    }),
    {
      id: 'label-new',
      label: 'New label…',
      icon: 'plus' as const,
      separated: allLabels.length + labels.length > 0,
      onAction: () => {
        setNewLabel('');
        setLabelError('');
        setLabelling(true);
      },
    },
  ];

  const addLabel = async () => {
    const name = newLabel.trim();
    if (!name) return;
    const problem = labelProblem(name);
    if (problem) {
      setLabelError(problem);
      return;
    }
    const refused = await onLabel(detail.id, name, true);
    if (refused) setLabelError(refused);
    else setLabelling(false);
  };

  const send = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    setReplyError('');
    try {
      setPending(await onReply(last, replyText, replyAll, reply.attachments));
    } catch (err) {
      setReplyError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  const cancelSend = async () => {
    if (!pending) return;
    const held = pending;
    setPending(null);
    setReplyError('');
    try {
      await held.cancel();
    } catch (err) {
      setReplyError(err instanceof Error ? err.message : String(err));
    }
  };

  // The reply as the composer carries on with it: the same recipients,
  // subject, quote and thread headers `onReply` would send, plus what has
  // been attached so far.
  const expandReply = () => {
    if (!identity) return;
    const draft = replyDraft(last, { identity, all: replyAll });
    onExpand({
      to: draft.to.map((p) => p.email).join(', '),
      ...(draft.cc?.length ? { cc: draft.cc.map((p) => p.email).join(', ') } : {}),
      subject: draft.subject,
      answering: { from: last.from[0]?.name || last.from[0]?.email || '', count: messages.length },
      body: `${replyText}${draft.text ?? ''}`,
      ...(draft.inReplyTo ? { inReplyTo: draft.inReplyTo } : {}),
      ...(draft.references ? { references: draft.references } : {}),
      attachments: reply.attachments,
    });
  };

  // The newest message, forwarded: the composer opens with its subject, the
  // introduction and quoted text, and the attachments it carried, and the
  // recipients still to be filled in.
  const forward = () => {
    if (!identity) return;
    const draft = forwardDraft(last, { identity });
    onExpand({
      subject: draft.subject,
      body: draft.text ?? '',
      ...(draft.attachments?.length ? { attachments: draft.attachments } : {}),
    });
  };

  return (
    <Panes.Column flex className="mail-reader">
      <div className="flex items-center gap-3 mail-pane-head">
        <Button
          kind="ghost"
          size="sm"
          iconOnly
          className="mail-back"
          aria-label="Back to the list"
          onPress={onBack}
        >
          <Icon name="back" size={18} />
        </Button>
        <span className="flex flex-col grow min-w-0" style={{ gap: 4 }}>
          <span className="h2">{detail.subject || '(no subject)'}</span>
          <span className="text-xs text-muted mail-head-meta">
            {joinParticipants(participants, me)} · {messages.length} message
            {messages.length === 1 ? '' : 's'} · started{' '}
            {formatStarted(messages[0]?.receivedAt ?? last.receivedAt)}
          </span>
        </span>
        <span className="flex items-center gap-2 flex-none">
          <span className="flex items-center gap-2 mail-head-acts">
            <Button
              size="sm"
              iconOnly
              className="mail-head-btn"
              aria-label="Archive"
              onPress={() => onArchive(detail.id)}
              disabled={busy}
            >
              <ArchiveIcon size={18} />
            </Button>
            <Button
              size="sm"
              iconOnly
              className="mail-head-btn"
              aria-label="Delete"
              onPress={() => onTrash(detail.id)}
              disabled={busy}
            >
              <Icon name="trash" size={18} />
            </Button>
            <Menu
              label="Snooze"
              placement="bottom end"
              trigger={
                <Button
                  size="sm"
                  iconOnly
                  className="mail-head-btn"
                  aria-label="Snooze"
                  disabled={busy}
                >
                  <Icon name="clock" size={18} />
                </Button>
              }
              items={snoozeItems}
            />
            <Menu
              label="Label"
              placement="bottom end"
              trigger={
                <Button size="sm" iconOnly className="mail-head-btn" aria-label="Label">
                  <Icon name="hash" size={18} />
                </Button>
              }
              items={labelItems}
            />
            <Button
              size="sm"
              iconOnly
              className="mail-head-btn"
              aria-label="Forward"
              onPress={forward}
              disabled={!identity}
            >
              <Icon name="forward" size={18} />
            </Button>
          </span>
          <Menu
            label="More"
            placement="bottom end"
            trigger={
              <Button size="sm" iconOnly className="mail-head-btn" aria-label="More">
                <Icon name="more" size={18} />
              </Button>
            }
            items={[
              // Below the fold `mail.css` hides the head's Archive and Delete
              // buttons; the menu carries them instead.
              ...(narrow
                ? [
                    {
                      id: 'archive',
                      label: 'Archive',
                      icon: 'archive' as const,
                      onAction: () => onArchive(detail.id),
                    },
                    {
                      id: 'delete',
                      label: 'Delete',
                      icon: 'trash' as const,
                      onAction: () => onTrash(detail.id),
                    },
                  ]
                : []),
              ...(narrow
                ? [
                    {
                      id: 'snooze',
                      label: 'Snooze',
                      icon: 'clock' as const,
                      items: snoozeItems,
                    },
                    { id: 'label', label: 'Label', icon: 'hash' as const, items: labelItems },
                    {
                      id: 'forward',
                      label: 'Forward',
                      icon: 'forward' as const,
                      disabled: !identity,
                      onAction: forward,
                    },
                  ]
                : []),
              {
                id: 'unread',
                label: 'Mark as unread',
                icon: 'mail-open',
                onAction: () => onMarkUnread(detail.id),
              },
              ...(moveItems.length
                ? [{ id: 'move', label: 'Move to', icon: 'folder' as const, items: moveItems }]
                : []),
            ]}
          />
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-0 overflow-y-auto pt-2 px-6 pb-6">
        {hidden > 0 && (
          <button
            type="button"
            className="flex items-center gap-2 py-2 px-0 text-muted"
            onClick={() => setShowAll(true)}
          >
            <Icon name="chev-down" size={15} />
            <span className="text-sm">
              {hidden} earlier message{hidden === 1 ? '' : 's'}
            </span>
          </button>
        )}
        {messages.map((m, i) =>
          i < hidden ? null : collapsed.has(i) && !isOpened(m.id) ? (
            <CollapsedMessage
              key={m.id}
              message={m}
              onOpen={() => setOpenIds((ids) => new Set(ids).add(m.id))}
            />
          ) : (
            <OpenMessage
              key={m.id}
              message={m}
              isFlagged={isFlagged}
              onStar={() => onStar(detail.id, !isFlagged)}
              onDownload={onDownload}
              client={client}
              calendarClient={calendarClient}
              identity={identity}
            />
          ),
        )}
        <div className="card mail-reply">
          <div className="flex items-center gap-2 mail-reply-head">
            <Avatar name={meName} seed={me} size="sm" />
            <span className="text-sm text-muted grow min-w-0">
              Reply to{' '}
              {joinParticipants(
                participants.filter((p) => p.email.toLowerCase() !== me.toLowerCase()),
                me,
              ) || displayName(last.from[0] ?? { email: '' })}
            </span>
            <Select
              size="sm"
              value={replyAll ? 'all' : 'one'}
              onChange={(e) => setReplyAll(e.target.value === 'all')}
              aria-label="Reply scope"
            >
              <option value="one">Reply</option>
              <option value="all">Reply all</option>
            </Select>
            <Button
              kind="ghost"
              size="sm"
              iconOnly
              aria-label="Open as a full message"
              onPress={expandReply}
              disabled={!identity}
            >
              <Icon name="expand" size={18} />
            </Button>
          </div>
          <div className="mail-reply-box">
            <textarea
              className="mail-reply-input"
              placeholder="Write your reply…"
              value={replyText}
              readOnly={Boolean(pending)}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !pending) {
                  e.preventDefault();
                  void send();
                }
              }}
              aria-label="Your reply"
            />
            <AttachmentChips
              attachments={reply.attachments}
              onRemove={reply.remove}
              uploading={reply.busy}
            />
          </div>
          {(replyError || reply.error) && (
            <p className="text-xs text-bad m-0 mail-reply-error" role="alert">
              {replyError || reply.error}
            </p>
          )}
          <div className="flex items-center gap-2 mail-reply-foot">
            <AttachButton
              onFiles={(files) => void reply.add(files)}
              disabled={reply.busy || sending || Boolean(pending)}
            />
            <div className="grow min-w-0" />
            <span className="text-xs text-muted whitespace-nowrap mail-reply-hint">
              Sends in 10 seconds — time to change your mind
            </span>
            <Button
              kind={pending ? 'default' : 'primary'}
              size="sm"
              disabled={pending ? false : !replyText.trim() || sending || reply.busy || !identity}
              onPress={() => void (pending ? cancelSend() : send())}
            >
              <Icon name={pending ? 'close' : 'send'} size={18} />
              <span>{pending ? 'Cancel' : sending ? 'Sending…' : 'Send'}</span>
            </Button>
          </div>
        </div>
      </div>
      {labelling && (
        <Dialog
          title="New label"
          onCancel={() => setLabelling(false)}
          actions={
            <>
              <Button kind="ghost" onPress={() => setLabelling(false)}>
                Cancel
              </Button>
              <Button kind="primary" disabled={!newLabel.trim()} onPress={() => void addLabel()}>
                Add label
              </Button>
            </>
          }
        >
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            aria-label="Label name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addLabel();
            }}
          />
          {labelError && (
            <p className="text-xs text-bad m-0 pt-1" role="alert">
              {labelError}
            </p>
          )}
        </Dialog>
      )}
    </Panes.Column>
  );
}

function CollapsedMessage({ message, onOpen }: { message: Message; onOpen: () => void }) {
  const from = message.from[0] ?? { email: 'unknown' };
  return (
    <button type="button" className="flex items-center gap-3 mail-msg-collapsed" onClick={onOpen}>
      <PersonIdentity name={from.name} address={from.email} email={from.email} size="sm" />
      <span className="text-sm text-muted truncate grow min-w-0">
        {bodyParagraphs(message)[0] ?? ''}
      </span>
      <span className="text-xs text-muted flex-none whitespace-nowrap">
        {formatMessageWhen(message.receivedAt)}
      </span>
    </button>
  );
}

function OpenMessage({
  message,
  isFlagged,
  onStar,
  onDownload,
  client,
  calendarClient,
  identity,
}: {
  message: Message;
  isFlagged: boolean;
  onStar: () => void;
  onDownload: (attachment: Attachment) => void;
  client: MailClient;
  calendarClient: CalendarClient;
  identity: MailIdentity | null;
}) {
  const from = message.from[0] ?? { email: 'unknown' };
  return (
    <div className="flex flex-col mail-msg">
      <div className="flex items-start gap-3">
        <span className="flex flex-col grow min-w-0 gap-0.25">
          <PersonIdentity name={from.name} address={from.email} email={from.email} kind="full" />
        </span>
        <span className="text-xs text-muted flex-none whitespace-nowrap">
          {formatMessageWhen(message.receivedAt)}
        </span>
        <Button
          kind="ghost"
          size="sm"
          iconOnly
          aria-label={isFlagged ? 'Unstar' : 'Star'}
          onPress={onStar}
        >
          <Icon name={isFlagged ? 'star-filled' : 'star'} size={18} />
        </Button>
      </div>
      <div className="flex flex-col mail-msg-body">
        {bodyParagraphs(message).map((p, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a message's paragraphs never reorder
          <p key={`${message.id}-${i}`} className="text-sm m-0 whitespace-pre-wrap">
            <Linkified text={p} />
          </p>
        ))}
        {message.attachments.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {message.attachments.map((a) =>
              isInvite(a) ? (
                <Fragment key={a.blobId}>
                  <CounterAttachment
                    client={client}
                    calendarClient={calendarClient}
                    identity={identity}
                    message={message}
                    fallback={<AttachmentChip attachment={a} onDownload={onDownload} />}
                  />
                  <InviteExtras client={client} calendarClient={calendarClient} attachment={a} />
                </Fragment>
              ) : (
                <AttachmentChip key={a.blobId} attachment={a} onDownload={onDownload} />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Today's bare `.ics` (or any other) attachment chip -- `CounterAttachment`'s
 *  own `fallback` for a calendar part that is not a `COUNTER` against an
 *  event this account organises, and what every other attachment still
 *  renders as. */
function AttachmentChip({
  attachment,
  onDownload,
}: {
  attachment: Attachment;
  onDownload: (attachment: Attachment) => void;
}) {
  return (
    <Button size="sm" onPress={() => onDownload(attachment)}>
      <Icon name={isInvite(attachment) ? 'calendar' : 'file'} size={15} />
      <span>{attachment.name}</span>
      <Icon name="download" size={14} />
    </Button>
  );
}

/**
 * A paragraph with its own `http(s)://` runs drawn as real links -- text
 * only, never HTML: `linkify` only ever splits the plain string `bodyParagraphs`
 * already returned, so nothing here parses or renders the body as markup.
 *
 * A run that names an Agora room (`routes.ts`'s own `parseRoomUrl`, agora#222)
 * opens in place, the same client-side navigation `go` performs everywhere
 * else in this app -- every other link still opens in a new tab, exactly as
 * it always has.
 */
function Linkified({ text }: { text: string }) {
  return (
    <>
      {linkify(text).map((segment, i) => {
        if (!('url' in segment)) {
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, ordered run across one paragraph, never reordered -- the same call `FreeBusy`'s own slots make.
          return <Fragment key={i}>{segment.text}</Fragment>;
        }
        const room = parseRoomUrl(segment.url, window.location.origin);
        if (room) {
          return (
            <a
              // biome-ignore lint/suspicious/noArrayIndexKey: see above
              key={i}
              href={segment.url}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                navigate(pathForRoute(room));
              }}
            >
              {segment.url}
            </a>
          );
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          <a key={i} href={segment.url} target="_blank" rel="noreferrer noopener">
            {segment.url}
          </a>
        );
      })}
    </>
  );
}

/**
 * `attachment.blobId` -> this invitation's own event, once found. It is
 * module-level, not component state, so a star, a reply or a cancelled send
 * (which refetch the thread and hand every attachment a new object with the
 * same `blobId`) and a remount do not download the `.ics` and list two years
 * of events again (agora#222 refuter finding). Only a found event is kept:
 * "not on your calendars" can change as soon as the invitation is accepted,
 * so that answer is looked up again next time. Exported so a test can clear
 * it between cases.
 */
export const eventByBlobId = new Map<string, CalendarEvent>();

type InviteEvent = 'loading' | 'missing' | CalendarEvent;

/**
 * "Open in Calendar" for any invitation `isInvite` recognises (agora#222),
 * not only the `COUNTER` proposals `CounterAttachment` above already turns
 * into their own card -- an ordinary meeting invite (`REQUEST`) never
 * reaches `parseImip`/`imipOf` at all (both answer `null` for anything but a
 * `COUNTER`/`DECLINECOUNTER`, `counter.ts`'s own doc comment), so its event is
 * found here instead, straight off the `.ics` bytes' own `UID`
 * (`counter.ts`'s `icsUid`), the same way `findOrganizedEvent` finds one for
 * a countered event, just without that one's organiser-only restriction.
 *
 * Nothing renders while still resolving, matching `CounterAttachment`'s own
 * "nothing until resolved" shape just above; found or not, the answer is a
 * disabled button with a reason once it is not on my calendars at all.
 */
function useInviteEvent(
  client: MailClient,
  calendarClient: CalendarClient,
  attachment: Attachment,
): InviteEvent {
  const [resolution, setResolution] = useState<InviteEvent>(
    () => eventByBlobId.get(attachment.blobId) ?? 'loading',
  );

  // Deliberately keyed on `attachment.blobId`, not the `attachment` object
  // itself: `Mail.tsx`'s own `detailKey` bump hands every attachment on a
  // thread a fresh object on every star, reply or cancelled send even
  // though its `blobId` never changed, and depending on the object would
  // re-download and re-look-up an invitation nothing about actually changed
  // (agora#222 refuter finding).
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const cached = eventByBlobId.get(attachment.blobId);
    if (cached) {
      setResolution(cached);
      return;
    }
    let live = true;
    setResolution('loading');
    downloadAttachment(client, attachment)
      .then((blob) => blob.text())
      .then((ics) => {
        const uid = icsUid(ics);
        return uid ? findEventByUid(calendarClient, uid) : null;
      })
      .then((event) => {
        // Only a found event is kept: "not on your calendars" can change
        // the moment the invitation is accepted, so it is looked up again.
        if (event) eventByBlobId.set(attachment.blobId, event);
        if (live) setResolution(event ?? 'missing');
      })
      .catch(() => {
        if (live) setResolution('missing');
      });
    return () => {
      live = false;
    };
  }, [client, calendarClient, attachment.blobId]);
  return resolution;
}

/** "Open in Calendar" (the button): nothing while `resolution` is still
 *  loading, a disabled button with its reason once the event is not on my
 *  calendars at all. */
function OpenInCalendarButton({ resolution }: { resolution: InviteEvent }) {
  if (resolution === 'loading') return null;
  if (resolution === 'missing') {
    return (
      <span title="Not on your calendars">
        <Button size="sm" disabled>
          <Icon name="calendar" size={15} />
          <span>Open in Calendar</span>
        </Button>
      </span>
    );
  }
  const event = resolution;
  return (
    <Button
      size="sm"
      onPress={() => {
        const agoraId = currentAgoraId();
        if (!agoraId) return;
        navigate(pathForCalendar(agoraId, { date: eventDateParam(event), eventId: event.id }));
      }}
    >
      <Icon name="calendar" size={15} />
      <span>Open in Calendar</span>
    </Button>
  );
}

/**
 * What an invitation's `.ics` is drawn as beyond the plain chip: the
 * "Going?" card when the event is on my calendars and I am a guest on it,
 * and always "Open in Calendar". One lookup serves both, so the `.ics` is
 * read once. An answer is written back to the cache, so reopening the
 * thread shows what was chosen rather than what was found first.
 */
function InviteExtras({
  client,
  calendarClient,
  attachment,
}: {
  client: MailClient;
  calendarClient: CalendarClient;
  attachment: Attachment;
}) {
  const resolution = useInviteEvent(client, calendarClient, attachment);
  return (
    <>
      {resolution !== 'loading' && resolution !== 'missing' && (
        <InvitationCard
          calendarClient={calendarClient}
          event={resolution}
          onAnswered={(answered) => eventByBlobId.set(attachment.blobId, answered)}
        />
      )}
      <OpenInCalendarButton resolution={resolution} />
    </>
  );
}
